import React, { useRef } from 'react';
import { getText } from '@storebrand-digital/fms-translations';

import DocumentTableRow from './Document.Table.Row';
import { useDocumentsWithPagination } from './hooks';
import {
  TableBody,
  TableCell,
  TableRow,
  AlertInline,
  Chip,
} from '@storebrand-digital/pretzel';
import { InfoFillIcon } from '@storebrand-digital/pretzel/icons';
import { useDocumentContext } from './context';
import { getColumns } from './Document.columns';
import DocumentTableSkeletonRow from './Document.Table.SkeletonRow';
import { archiveSegments, Segment } from '../../utils/segments';
import dashboardStyles from '../Dashboard/Dashboard.module.css';
import { useKron } from '../../app/hooks/useKron';

function DocumentsTableBody() {
  const { data, isError, isLoading, isFetching } = useDocumentsWithPagination();
  const { selectedSegmentId } = useDocumentContext();
  const columns = getColumns(selectedSegmentId);
  const colSpan = columns.length;
  const lastRowCount = useRef(15);
  const { isKron } = useKron();
  if (isLoading || isFetching) {
    return (
      <TableBody>
        {Array(lastRowCount.current)
          .fill('')
          .map((_, index) => (
            <DocumentTableSkeletonRow key={`table-row-loading-${index}`} />
          ))}
      </TableBody>
    );
  }

  if (isError) {
    lastRowCount.current = 1;
    return (
      <TableBody>
        <TableRow>
          <TableCell className="table-cell-error" colSpan={colSpan}>
            <AlertInline
              heading={getText('error.table.fetchDocuments')}
              variant="error"
              role="status"
            />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  if (data?.documents?.length === 0) {
    lastRowCount.current = 1;
    return (
      <TableBody>
        <TableRow>
          <TableCell className="table-cell-error" colSpan={colSpan}>
            {selectedSegmentId === Segment.MAIN ? (
              <div className={dashboardStyles.segmentedChipsNoContent}>
                <div className="alertTitle">
                  <InfoFillIcon />
                  <p className="font-body-sm-demibold">
                    {getText('documents.notfound.main')}
                  </p>
                </div>

                <div className={dashboardStyles.segmentedChipsBase}>
                  {archiveSegments
                    .filter((segment) => segment.id !== Segment.MAIN)
                    .filter((segment) => segment.showInKron || !isKron)
                    .map((segment) => (
                      <Chip
                        key={segment.id}
                        id={`chips-${segment.id}`}
                        variant="input"
                        value={segment.id}
                        label={getText(segment.label)}
                        density="compact"
                        iconLeft={segment.icon}
                        onValueChange={() => {
                          /* no-op */
                        }}
                        labelProps={{
                          'data-trackid': `no_content_chip-segment_${segment.id}`,
                        }}
                        checked={false}
                      />
                    ))}
                </div>
              </div>
            ) : (
              <AlertInline
                heading={getText('documents.notfound')}
                variant="info-subtle"
                role="status"
              />
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  lastRowCount.current = data?.documents.length as number;
  return (
    <TableBody>
      {data?.documents?.map((document) => (
        <DocumentTableRow key={document.id} {...document} />
      ))}
    </TableBody>
  );
}

export default DocumentsTableBody;
